import color from '@deprecated-ui/_style/color';
import { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import Image from 'global/Image';
import { CompanyChartData } from 'lib/entities/company.types';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { useMemo } from 'react';
import { EmptyChartContainer, EmptyChartText } from '../../styled';

interface StockChartProps {
  chart: CompanyChartData;
}

function StockChart({ chart }: StockChartProps) {
  const { prices = [], previous = 0 } = chart ?? {};

  const fillColor = color.clearDay;
  const lineColor = color.mountainMeadow;

  const data: ChartData<'line'> = {
    labels: prices.map((price) =>
      price.formatted_date && price.date !== '0'
        ? moment(price.formatted_date).format('HH:mm')
        : '',
    ),
    datasets: [
      {
        fill: true,
        tension: 0,
        backgroundColor: fillColor,
        borderColor: lineColor,
        borderCapStyle: 'butt',
        borderDashOffset: 0.0,
        borderDash: [],
        borderJoinStyle: 'miter',
        borderWidth: 1.75,
        data: prices.map((pr) => parseInt(pr.value, 10)),
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    layout: {
      padding: {
        top: 40,
        right: 10,
        left: 10,
        bottom: 10,
      },
    },
    animation: {
      duration: 0,
    },
    resizeDelay: 0, // animation duration after a resize
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      y: {
        position: 'right',
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          maxTicksLimit: 5,
          callback(val) {
            let price = val;
            // if price contain decimal, then hide tick
            if (typeof price === 'number' && price % 1 !== 0) price = null;
            return price || null;
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          align: 'start',
          maxTicksLimit: 5,
          maxRotation: 0,
          callback(val) {
            // @ts-ignore
            // eslint-disable-next-line react/no-this-in-sfc
            return this.getLabelForValue(Number(val)) || null;
          },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  const finalChartData = useMemo(() => {
    if (prices.length === 0) {
      return {
        datasets: [
          {
            data: previous ? [previous, previous] : [],
            borderColor: '#E5E5E5',
            borderDash: [4, 4],
            borderWidth: 1,
            fill: false,
          },
        ],
        labels: ['', ''],
      };
    }

    return data;
  }, [data, previous, prices]);

  if (chart === null) {
    return (
      <EmptyChartContainer>
        <Image src="/illustration/chart-unavailable.svg" alt="Empty Chart" />
        <EmptyChartText>Data Chart Belum Tersedia</EmptyChartText>
      </EmptyChartContainer>
    );
  }

  return <Line data={finalChartData} options={options} />;
}

export default StockChart;
